import React, { useMemo } from 'react';
import {
  Box,
  Drawer,
  Grid,
  Typography,
  IconButton,
  Chip,
  Paper,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  Collapse,
  ListItemButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import SaveIcon from '@mui/icons-material/Save';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useDebugPanel } from './hooks';

interface DebugPanelProps {
  open: boolean;
  onClose: () => void;
  assistantState: any;
}

const DebugPanel: React.FC<DebugPanelProps> = ({
  open,
  onClose,
  assistantState,
}) => {
  const { getDebugData } = useDebugPanel({ assistantState });
  const debugData = useMemo(() => getDebugData(), [getDebugData, open]);
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(
    new Set(),
  );

  // Close on backdrop click
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const formatTimestamp = (date?: Date) => {
    if (!date) return 'Never';
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const toggleExpanded = (key: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  return (
    <>
      {open && (
        <Box
          onClick={handleBackdropClick}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1200,
          }}
        />
      )}
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            height: '75vh',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            boxShadow: '0px -4px 20px rgba(0, 0, 0, 0.1)',
          },
        }}
        ModalProps={{
          keepMounted: true,
          BackdropProps: { invisible: true },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Listing Assistant Debug Panel
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Save Status Indicator */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SaveIcon
                  sx={{
                    color: debugData.listingAssistant.changesSinceSave
                      ? 'warning.main'
                      : 'success.main',
                  }}
                />
                <Box>
                  <Typography variant="caption" sx={{ display: 'block' }}>
                    Last Save:{' '}
                    {formatTimestamp(debugData.listingAssistant.lastSaveTime)}
                  </Typography>
                  {debugData.listingAssistant.changesSinceSave && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'warning.main', display: 'block' }}
                    >
                      Unsaved changes
                    </Typography>
                  )}
                </Box>
              </Box>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
            <Grid container spacing={3}>
              {/* Status Flags */}
              <Grid size={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: 'text.secondary' }}
                >
                  Status Flags
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(debugData.statusFlags).map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${key}: ${value ? 'true' : 'false'}`}
                      size="small"
                      color={value ? 'success' : 'default'}
                      variant={value ? 'filled' : 'outlined'}
                    />
                  ))}
                </Box>
              </Grid>

              {/* Listing Assistant Metrics */}
              <Grid size={12}>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: 'text.secondary' }}
                >
                  Listing Assistant Metrics
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(debugData.listingAssistant.metrics).map(
                    ([key, value]) => (
                      <Chip
                        key={key}
                        label={`${key.replace(/_/g, ' ')}: ${value}`}
                        size="small"
                        variant="outlined"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    ),
                  )}
                </Box>
              </Grid>

              {/* Needs Review Fields */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper sx={{ p: 2, height: '100%', bgcolor: 'warning.light' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Needs Review Fields ({debugData.needsReview.totalCount})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`Resolved: ${debugData.needsReview.resolvedCount}`}
                      size="small"
                      color="success"
                    />
                    <Chip
                      icon={<WarningIcon />}
                      label={`Pending: ${debugData.needsReview.pendingCount}`}
                      size="small"
                      color="warning"
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {debugData.needsReview.fields.map((field) => (
                      <Box key={field.key}>
                        <ListItemButton
                          onClick={() => toggleExpanded(field.key)}
                          sx={{
                            bgcolor: field.isResolved
                              ? 'success.light'
                              : 'warning.dark',
                            mb: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                {field.isResolved ? (
                                  <CheckCircleIcon
                                    sx={{ fontSize: 16, color: 'success.dark' }}
                                  />
                                ) : (
                                  <WarningIcon
                                    sx={{ fontSize: 16, color: 'warning.main' }}
                                  />
                                )}
                                <Typography
                                  variant="caption"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {field.name} ({field.key})
                                </Typography>
                                {field.autoResolved && (
                                  <Chip
                                    label="Auto"
                                    size="small"
                                    color="info"
                                    sx={{ height: 16, fontSize: '0.7rem' }}
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ display: 'block' }}
                                >
                                  Conflicts: {field.options.length} options
                                </Typography>
                                {field.isResolved && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: 'success.dark',
                                    }}
                                  >
                                    Resolved to:{' '}
                                    {JSON.stringify(field.resolvedValue)}
                                  </Typography>
                                )}
                                {field.currentContextValue !== null &&
                                  field.currentContextValue !== undefined && (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        color: 'info.dark',
                                      }}
                                    >
                                      In Context:{' '}
                                      {JSON.stringify(
                                        field.currentContextValue,
                                      )}
                                    </Typography>
                                  )}
                              </Box>
                            }
                          />
                          {expandedItems.has(field.key) ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </ListItemButton>
                        <Collapse
                          in={expandedItems.has(field.key)}
                          timeout="auto"
                          unmountOnExit
                        >
                          <List component="div" disablePadding sx={{ pl: 2 }}>
                            {field.options.map((option, idx) => (
                              <ListItem
                                key={idx}
                                sx={{
                                  bgcolor: 'grey.100',
                                  mb: 0.5,
                                  borderRadius: 1,
                                }}
                              >
                                <ListItemText
                                  primary={`Option ${idx + 1}: ${option.label}`}
                                  secondary={
                                    <Box>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Value: {JSON.stringify(option.value)}
                                      </Typography>
                                      <Typography
                                        variant="caption"
                                        sx={{ display: 'block' }}
                                      >
                                        Record ID: {option.recordId}
                                      </Typography>
                                    </Box>
                                  }
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Ready to Import Fields */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Ready to Import Fields ({debugData.readyToImport.count})
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={`Saved: ${debugData.readyToImport.savedCount}`}
                      size="small"
                      color="success"
                    />
                    <Chip
                      icon={<PendingIcon />}
                      label={`Pending: ${debugData.readyToImport.pendingCount}`}
                      size="small"
                      color="warning"
                    />
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  <List dense sx={{ maxHeight: 300, overflow: 'auto' }}>
                    {debugData.readyToImport.fields.map((field) => (
                      <ListItem
                        key={field.key}
                        sx={{
                          bgcolor: field.isInContext
                            ? 'success.light'
                            : 'warning.light',
                          mb: 0.5,
                          borderRadius: 1,
                        }}
                      >
                        <ListItemText
                          primary={
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              {field.isInContext ? (
                                <CheckCircleIcon
                                  sx={{ fontSize: 16, color: 'success.dark' }}
                                />
                              ) : (
                                <PendingIcon
                                  sx={{ fontSize: 16, color: 'warning.dark' }}
                                />
                              )}
                              <Typography
                                variant="caption"
                                sx={{ fontWeight: 600 }}
                              >
                                {field.key}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block' }}
                              >
                                Source: {field.source}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: 'block' }}
                              >
                                Value: {JSON.stringify(field.value)}
                              </Typography>
                              {field.isInContext &&
                                field.contextValue !== field.value && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: 'block',
                                      color: 'error.main',
                                    }}
                                  >
                                    Context Value:{' '}
                                    {JSON.stringify(field.contextValue)}
                                  </Typography>
                                )}
                            </Box>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>

              {/* Enabled Records */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Records
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                    {debugData.listingAssistant.enabledRecords.map((record) => (
                      <Box
                        key={record.id}
                        sx={{
                          p: 1,
                          mb: 1,
                          bgcolor: record.isEnabled
                            ? 'success.light'
                            : 'grey.200',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            display: 'block',
                            fontWeight: record.isEnabled ? 600 : 400,
                          }}
                        >
                          {record.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary' }}
                        >
                          Type: {record.type} | ID: {record.id.substring(0, 8)}
                          ...
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              {/* Draft Listing State */}
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Paper sx={{ p: 2, height: '100%', bgcolor: 'grey.50' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Draft Listing State
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography variant="caption">
                      Has Assistant:{' '}
                      {debugData.draftListingState.hasAssistant ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption">
                      Has Location:{' '}
                      {debugData.draftListingState.hasLocation ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption">
                      Loading Suggestions:{' '}
                      {debugData.draftListingState.isLoadingSuggestions
                        ? 'Yes'
                        : 'No'}
                    </Typography>
                    <Typography variant="caption">
                      Records Count: {debugData.draftListingState.recordsCount}
                    </Typography>
                    <Typography variant="caption">
                      Record Types:{' '}
                      {debugData.draftListingState.recordTypes.join(', ') ||
                        'None'}
                    </Typography>
                    {debugData.draftListingState.suggestionsError && (
                      <Alert severity="error" sx={{ mt: 1 }}>
                        {debugData.draftListingState.suggestionsError}
                      </Alert>
                    )}
                  </Box>
                </Paper>
              </Grid>

              {/* Listing State */}
              <Grid size={{ xs: 12, md: 6, lg: 3 }}>
                <Paper sx={{ p: 2, height: '100%', bgcolor: 'primary.light' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1, color: 'white' }}
                  >
                    Listing State
                  </Typography>
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                  >
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      Published:{' '}
                      {debugData.listingState.published ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      Locked: {debugData.listingState.locked ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      Discarded:{' '}
                      {debugData.listingState.discarded ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      From Live Listing:{' '}
                      {debugData.listingState.isFromLiveListing ? 'Yes' : 'No'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      Focused Fields:{' '}
                      {debugData.listingState.focusedFieldsCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.100' }}>
                      Hidden Fields: {debugData.listingState.hiddenFieldsCount}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Raw Listing Context */}
              <Grid size={12}>
                <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    Current Listing Context ({debugData.listingContext.count}{' '}
                    fields)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', display: 'block', mb: 1 }}
                  >
                    These values will be saved when "Create Draft Listing" is
                    clicked
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      maxHeight: 300,
                      overflow: 'auto',
                      bgcolor: 'grey.700',
                      color: 'white',
                      p: 2,
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      fontFamily: 'monospace',
                    }}
                  >
                    {JSON.stringify(debugData.listingContext.fields, null, 2)}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default DebugPanel;
